/**
 * Comparative Bar Chart Component
 * Horizontal bar chart for target vs actual comparison
 */

import React from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell
} from 'recharts'
import { chartTheme } from './chart-theme'
import { formatCurrency, calculateAttainment } from './chart-utils'
import { BaseChart } from './BaseChart'

export interface ComparativeBarData {
  name: string
  target: number
  actual: number
}

export interface ComparativeBarChartProps {
  data: ComparativeBarData[]
  height?: number
  loading?: boolean
  error?: Error | null
  onRetry?: () => void
  sortByAttainment?: boolean
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null

  const target = payload[0]?.value || 0
  const actual = payload[1]?.value || 0
  const attainment = calculateAttainment(actual, target)

  return (
    <div className="bg-[rgba(35,36,41,0.95)] border border-white/10 rounded-lg p-3">
      <p className="text-sm font-semibold text-foreground mb-1">
        {label}
      </p>
      <span className="text-xs block" style={{ color: chartTheme.colors.neutral }}>
        Meta: {formatCurrency(target)}
      </span>
      <span className="text-xs block" style={{ color: chartTheme.colors.primary }}>
        Real: {formatCurrency(actual)}
      </span>
      <span
        className="text-xs block font-semibold mt-1"
        style={{
          color: attainment >= 90 ? chartTheme.colors.positive : chartTheme.colors.warning
        }}
      >
        Atingimento: {attainment.toFixed(1)}%
      </span>
    </div>
  )
}

export const ComparativeBarChart: React.FC<ComparativeBarChartProps> = ({
  data,
  height = 400,
  loading = false,
  error = null,
  onRetry,
  sortByAttainment = true
}) => {
  // Sort by attainment if requested
  const sortedData = sortByAttainment
    ? [...data].sort((a, b) => {
        const attainmentA = calculateAttainment(a.actual, a.target)
        const attainmentB = calculateAttainment(b.actual, b.target)
        return attainmentB - attainmentA
      })
    : data

  return (
    <BaseChart
      loading={loading}
      error={error}
      onRetry={onRetry}
      width="100%"
      height={height}
      ariaLabel="Horizontal bar chart comparing target vs actual performance"
    >
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={sortedData} layout="horizontal">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />

          <XAxis
            type="number"
            stroke={chartTheme.colors.textSecondary}
            style={{ fontSize: 12 }}
            tickFormatter={(value) => formatCurrency(value)}
          />

          <YAxis
            type="category"
            dataKey="name"
            stroke={chartTheme.colors.textSecondary}
            style={{ fontSize: 12 }}
            width={150}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: 12
            }}
          />

          <Bar
            dataKey="target"
            name="Meta"
            fill={chartTheme.colors.neutral}
            radius={[0, 4, 4, 0]}
            animationDuration={300}
          />

          <Bar
            dataKey="actual"
            name="Real"
            radius={[0, 4, 4, 0]}
            animationDuration={300}
          >
            {sortedData.map((entry, index) => {
              const attainment = calculateAttainment(entry.actual, entry.target)
              const color =
                attainment >= 90
                  ? chartTheme.colors.positive
                  : attainment >= 70
                  ? chartTheme.colors.warning
                  : chartTheme.colors.negative

              return <Cell key={`cell-${index}`} fill={color} />
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </BaseChart>
  )
}
